import React, { memo, useEffect, useRef, useState } from "react";
import { useInViewport, useScroll } from "ahooks";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import { StickyContainer, Sticky } from "react-sticky";
import { useLocalStorageState } from "ahooks-v2";
import { Button } from "@t8891/pc-v1";
import { host } from "@Services/url";
import { fetchSummaryKindInfo } from "@Services/api";
import { gaEvent } from "@Lib/ga";

const defaulte = {
  price: "-",
  gas: "-",
  oil: "-",
  speed: "-",
  autoType: "-",
  views: "-",
  articleCount: 0,
  trialartCount: 0,
  buycarMenuCount: 0,
  movieCount: 0,
  ratingCount: 0,
  isInquiry: 0,
  discountCount: 0,
  inquiryCount: "-",
  dealerCount: "-",
  sales: "-",
  uriPrefix: "-",
  rank: "-",
  usedCar: {
    brandId: 0,
    price: "",
    count: "-",
    kindId: 0,
    url: "",
    filter: "-",
  },
  singlePublicPraise: [],
  prevMonth: "-",
  newKindId: 0,
  kindId: 0,
  brandId: 0,
  willSale: -1,
  saleStop: -1,
};

type IAskPrice = {
  kindId: string;
  brandIds: string;
  kindName?: string;
  brandName?: string;
};
const AskPrice: React.FC<IAskPrice> = ({ kindId, brandIds, brandName, kindName }) => {

  return (
    <section className='text-14 text-[#fff] absolute right-10 top-0 flex justify-around h-44 items-center'>
      <div className='mr-10 leading-[15px]'>
        <div>{window.brandName || brandName}</div>
        <div>{window.kindName || kindName}</div>
      </div>
      <Button
        className='w-[87px] h-28 text-14 rounded-20'
        inline
        radius
        style={{ lineHeight: "12px" }}
        type='warning'
        size='large'
        onClick={() => {
          window.open(
            `${host.c}/ask/price?b=${window.brandId || brandIds}&k=${window.kindId || kindId}&source=置頂導航-一鍵詢價`,
            "_blank",
          );
        }}
      >
        一鍵詢價
      </Button>
    </section>
  );
};

interface IProps {
  kindId: string;
  brandIds?: string;
  brandName?: string;
  kindName?: string;
  newKindId: string;
  isNavSticky?: boolean;
  css?: any;
}

type IItem = {
  name: string;
  url: string;
  press: boolean;
  dot?: boolean;
  onClick?: () => void;
};
const Item: React.FC<IItem> = ({ url, name, press, dot, onClick }) => {
  const [visible, setVisible] = useLocalStorageState(`kindsum-${name}`, "false");

  return (
    <a
      className={`px-20 text-16 leading-44 h-full hover:text-[#fff] text-[#fff] relative ${
        press ? "bg-[#ff963c]" : ""
      }`}
      href={url}
      target={`${url !== "###" ? "_blank" : ""}`}
      onClick={() => {
        onClick && onClick();
        setVisible("true");
        gaEvent(`綜述-導航`, name.replace("/", ""), ``);
      }}
    >
      {name}
      {dot && visible !== "true" && (
        <i className='w-[5px] h-[5px] bg-[#ff0000] rounded-full absolute right-12 top-10' />
      )}
    </a>
  );
};

const KindSumNav: React.FC<IProps> = ({
  kindId,
  newKindId,
  isNavSticky = false,
  css = {},
  brandIds,
  brandName,
  kindName,
}) => {
  const pathname = location.pathname;
  const [data, setData] = useState<any>(defaulte);
  const [isMounted, setIsMounted] = useState(false);
  
  const scroll = useScroll(document);

  const getData = async () => {
    try {
      const res = await fetchSummaryKindInfo({
        k: kindId,
        nk: newKindId,
      });

      if (res?.status === 12000) {
        setData(res?.data);
        setIsMounted(true);
      } else {
        throw Error("fetch Error");
      }
    } catch (error) {
      setIsMounted(true);
    }
  };

  const { uriPrefix, articleCount, trialartCount, ratingCount, dealerCount, usedCar, isInquiry } = data;

  useEffect(() => {
    if (isMounted) return;
    if (Number(kindId) && Number(newKindId)) {
      getData();
    }
  }, [kindId, newKindId]);

  if (!isMounted) {
    return (
      <SkeletonTheme baseColor='#3264ff'>
        <Skeleton count={1} style={{ width: "1190px", height: "44px" }} enableAnimation={false} />
      </SkeletonTheme>
    );
  }

  return (
    <>
      <section className={`w-[1190px] h-44 opacity-100 bg-[#3264ff] my-0 mx-auto flex relative`} id='KindSumTopNav'>
        <Item name='綜述' url={`${uriPrefix}/Summary.html`} press={pathname.includes("Summary")} />
        <Item name='圖片' url={`${uriPrefix}/HDPhoto.html`} press={pathname.includes("HDPhoto")} />
        <Item name='規格/配備' url={`${uriPrefix}/Specification.html`} press={pathname.includes("Specification")} />
        {(trialartCount > 0 || articleCount > 0) && (
          <Item name='資訊' url={`${uriPrefix}/information.html`} press={pathname.includes("information")} />
        )}
        <Item name='車友交流' url={`${uriPrefix}/Comment.html`} press={pathname.includes("Comment")} />
        {data?.buycarMenuCount > 0 && (
          <Item name='菜單' dot={true} url={`${host.c}/menu${uriPrefix}`} press={pathname.includes("sharedorder")} />
        )}
        {dealerCount > 0 && (
          <Item name='展示間' url={`${uriPrefix}/Showroom.html`} press={pathname.includes("Showroom")} />
        )}
        {isInquiry === 1 && (
          <Item name='業代' url={`${host.c}/agent/business?brandId=${window.brandId || brandIds}`} press={false} />
        )}
        {usedCar.price && <Item name='中古車' url={usedCar.url} press={false} />}
        <Item name='高價收車' url={`${host.auto}/sell-spread.html?source=8891&entry=nc_op_hp_hn_hcp`} press={false} />
        <Item name='二手車估價' url={`${host.auto}/carResource/valuation?source=8891&entry=nc_op_hp_hn_ucv`} press={false} />
        {data.isInquiry === 1 && scroll?.top > 167 && (
          <AskPrice kindId={kindId} brandIds={String(brandIds)} brandName={brandName} kindName={kindName} />
        )}
      </section>
    </>
  );
};
export default memo(KindSumNav);
